import React, { useContext, useEffect, useRef } from 'react';
import { v4 as uuid } from 'uuid';
import filesize from 'filesize';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { format, isValid } from 'date-fns';
import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  FormHelperText,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { toast } from 'react-toastify';
import Toast from '../../../../components/Toast';
import { options } from '../../../../helpers/toastOptions';
import { StepButtons } from '../StepButtons';
import { Declaration } from './components/Declaration';
import { FileList } from './components/FileList';
import { OtherFiles } from './components/OtherFiles';
import {
  Title,
  TooltipWrapper,
  TooltipIcon,
  LabelFileInput,
  FileInput,
  BoxButton
} from './styled';
import { ReverseCollectContext } from '../..';
import { usePickup } from '../../../../hooks/Pickup';

export const TaxInfo = () => {
  const { acceptDeclaration } = usePickup();

  const inputFileRef = useRef(null);

  const maxSM = useMediaQuery('(max-width:768px)');
  const maxMD = useMediaQuery('(max-width:900px)');

  const {
    documentType,
    setDocumentType,
    fileAmount,
    setFileAmount,
    declarationFiles,
    setDeclarationFiles,
    invoiceFiles,
    setInvoiceFiles,
    invoiceNumberErrorList,
    setInvoiceNumberErrorList,
    invoiceDateErrorList,
    setInvoiceDateErrorList,
    otherFiles,
    setOtherFiles,
    openAcceptResponsibilityTermsModal,
    hasAcceptResponsibilityTerms,
    acceptedInvoiceNotIssued,
    setAcceptedInvoiceNotIssued,
    openAcceptInvoiceNotIssuedModal,
    useForceUpdate,
    hasCreatePickupWithInvoice,
    enableDeclaration,
    setIsSubmitting
  } = useContext(ReverseCollectContext);

  const forceUpdate = useForceUpdate();

  const collectTaxInfoSchema = Yup.object().shape({
    // taxDocumentType: Yup.string().oneOf(
    //   ['declaration', 'invoiceIssued', 'invoiceNotIssued'],
    //   'Informe o tipo de documento'
    // ),
    acceptedInvoiceNotIssued: Yup.boolean().when('taxDocumentType', {
      is: 'invoiceNotIssued',
      then: Yup.boolean()
    })
  });

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(collectTaxInfoSchema)
  });

  const handleSetFileAmount = (value) => {
    setFileAmount(value);

    if (value === '') {
      return;
    }

    const newInvoiceFiles = [...invoiceFiles];

    if (value > newInvoiceFiles.length) {
      const steps = value - newInvoiceFiles.length;

      for (let i = 0; i < steps; i++) {
        newInvoiceFiles.push({
          id: uuid(),
          file: '',
          name: '',
          size: '',
          readableSize: '',
          invoiceNumber: '',
          invoiceDate: null
        });
      }

      setInvoiceFiles(newInvoiceFiles);
    } else if (value < newInvoiceFiles.length) {
      for (let i = newInvoiceFiles.length - 1; i >= value; i--) {
        if (newInvoiceFiles[i].file === '') {
          newInvoiceFiles.splice(-1);
        }
      }

      while (
        !!newInvoiceFiles?.find(({ file }) => file === '') &&
        newInvoiceFiles?.length > value
      ) {
        newInvoiceFiles?.reverse();

        const itemToRemove = newInvoiceFiles?.findIndex(
          ({ file }) => file === ''
        );

        if (itemToRemove >= 0) {
          newInvoiceFiles.splice(itemToRemove, 1);
        }

        newInvoiceFiles?.reverse();
      }

      setInvoiceFiles(newInvoiceFiles);
    }
  };

  const handleChangeRadio = (value) => {
    value === 'declaration' && handleSetFileAmount(declarationFiles.length);

    (value === 'invoiceIssued' || value === 'invoiceNotIssued') &&
      setDeclarationFiles([]);

    if (documentType !== 'invoiceIssued' && value === 'invoiceIssued') {
      const newInvoiceFiles = [];

      for (let i = 0; i < fileAmount; i++) {
        newInvoiceFiles.push({
          id: uuid(),
          file: '',
          name: '',
          size: '',
          readableSize: '',
          invoiceNumber: '',
          invoiceDate: null
        });
      }

      setInvoiceFiles(newInvoiceFiles);
    }

    setDocumentType(value);

    value !== 'invoiceNotIssued' && setAcceptedInvoiceNotIssued(false);
  };

  const handleAddInvoiceFiles = (event) => {
    const { files } = event.target;
    let showMessage = false;

    if (!fileAmount || fileAmount < 1) {
      showMessage = true;
    } else {
      const newInvoiceFiles = [...invoiceFiles];

      Array.from(files).forEach((file) => {
        if (file.type !== 'application/pdf' && file.type !== 'text/xml') {
          toast(
            <Toast
              title="Atenção!"
              content="Tipo de arquivo deve ser PDF ou XML."
              type="error"
            />,
            options
          );
          return;
        }

        let hasFile = false;
        newInvoiceFiles.forEach((newInvoiceFile, index) => {
          if (!newInvoiceFile.file && !hasFile) {
            hasFile = true;
            newInvoiceFiles[index] = {
              id: newInvoiceFile.id,
              file,
              name: file.name,
              size: file.size,
              readableSize: filesize(file.size),
              invoiceNumber: newInvoiceFile.invoiceNumber,
              invoiceDate: newInvoiceFile.invoiceDate
            };
          }
        });

        if (!hasFile) {
          showMessage = true;
        }
      });

      setInvoiceFiles(newInvoiceFiles);

      forceUpdate();
    }

    showMessage &&
      toast(
        <Toast
          title="Atenção!"
          content="A quantidade de documentos anexados neste campo não corresponde ao informado no campo “quantidade de documentos”."
          type="error"
        />,
        options
      );

    inputFileRef.current.value = '';
  };

  const onNextStep = (data) => {
    if (data.taxDocumentType === 'declaration') {
      if (
        declarationFiles.length === 0 &&
        !otherFiles?.find((file) => file.documentType === '3')
      ) {
        toast(
          <Toast
            title="Atenção!"
            content="Arquivo de declaração obrigatório."
            type="error"
          />,
          options
        );
        return;
      }

      if (
        (fileAmount &&
          `${
            declarationFiles.length +
            otherFiles?.filter((file) => file.documentType === '3')?.length
          }` !== `${fileAmount}`) ||
        (!fileAmount && declarationFiles.length > 0)
      ) {
        toast(
          <Toast
            title="Atenção!"
            content="A quantidade de documentos anexados não corresponde ao informado no campo “quantidade de documentos”."
            type="error"
          />,
          options
        );
        return;
      }
    }

    if (data.taxDocumentType === 'invoiceIssued') {
      let hasMissingInvoiceFile = false;
      let hasMissingInvoiceNumber = false;
      let hasInvalidInvoiceNumber = false;
      let hasMissingDate = false;
      let hasInvalidDate = false;

      if (
        (fileAmount && `${invoiceFiles.length}` !== `${fileAmount}`) ||
        (!fileAmount && invoiceFiles.length > 0)
      ) {
        toast(
          <Toast
            title="Atenção!"
            content="A quantidade de documentos anexados não corresponde ao informado no campo “quantidade de documentos”."
            type="error"
          />,
          options
        );

        return;
      }

      if (invoiceFiles.length > 0) {
        const newInvoiceNumberErrorList = [...invoiceNumberErrorList];
        const newInvoiceDateErrorList = [...invoiceDateErrorList];
        let emptyFile = true;
        let count = 0;

        invoiceFiles.forEach((invoice) => {
          if (!invoice.file) {
            hasMissingInvoiceFile = true;
          }
          if (invoice.file) {
            emptyFile = false;
            count++;
          }

          if (
            invoice.file &&
            (!invoice.invoiceNumber || invoice.invoiceNumber === '')
          ) {
            hasMissingInvoiceNumber = true;
            !newInvoiceNumberErrorList.includes(invoice.id) &&
              newInvoiceNumberErrorList.push(invoice.id);
          } else if (invoice.invoiceNumber <= 0) {
            hasInvalidInvoiceNumber = true;

            !newInvoiceNumberErrorList.includes(invoice.id) &&
              newInvoiceNumberErrorList.push(invoice.id);
          } else {
            const index = newInvoiceNumberErrorList.findIndex(
              (id) => id === invoice.id
            );

            index >= 0 && newInvoiceNumberErrorList.splice(index, 1);
          }

          if (invoice.invoiceDate === '' || invoice.invoiceDate === null) {
            hasMissingDate = true;

            !newInvoiceDateErrorList.includes(invoice.id) &&
              newInvoiceDateErrorList.push(invoice.id);
          } else if (
            !isValid(invoice.invoiceDate) ||
            format(new Date(invoice.invoiceDate), 'yyyy')?.replace(/^0+/, '')
              ?.length < 4
          ) {
            hasInvalidDate = true;

            !newInvoiceDateErrorList.includes(invoice.id) &&
              newInvoiceDateErrorList.push(invoice.id);
          } else {
            const index = newInvoiceDateErrorList.findIndex(
              (id) => id === invoice.id
            );

            index >= 0 && newInvoiceDateErrorList.splice(index, 1);
          }
        });

        if (count === 0) {
          setInvoiceNumberErrorList([]);
          setInvoiceDateErrorList([]);
        } else {
          setInvoiceNumberErrorList(newInvoiceNumberErrorList);
          setInvoiceDateErrorList(newInvoiceDateErrorList);
        }

        if (hasMissingInvoiceFile && emptyFile) {
          toast(
            <Toast
              title="Atenção!"
              content="Não foi anexado nenhum documento."
              type="error"
            />,
            options
          );
          return;
        }

        if (hasMissingInvoiceFile && fileAmount !== count) {
          toast(
            <Toast
              title="Atenção!"
              content="A quantidade de documentos anexados não corresponde ao informado no campo “quantidade de documentos”."
              type="error"
            />,
            options
          );
          return;
        }

        if (hasMissingInvoiceNumber) {
          toast(
            <Toast
              title="Atenção!"
              content="Informe o número de todas notas fiscais."
              type="error"
            />,
            options
          );
          return;
        }

        if (hasInvalidInvoiceNumber) {
          toast(
            <Toast
              title="Atenção!"
              content="Número inválido informado em uma das notas fiscais."
              type="error"
            />,
            options
          );
          return;
        }

        if (hasMissingDate) {
          toast(
            <Toast
              title="Atenção!"
              content="Informe a data de todas notas fiscais."
              type="error"
            />,
            options
          );
          return;
        }

        if (hasInvalidDate) {
          toast(
            <Toast
              title="Atenção!"
              content="A data de saída informada é inválida, deve estar no formato DD/MM/AAAA."
              type="error"
            />,
            options
          );
          return;
        }
      } else {
        hasMissingInvoiceFile = true;

        toast(
          <Toast
            title="Atenção!"
            content="Não foi anexado nenhum documento."
            type="error"
          />,
          options
        );
      }

      if (
        hasMissingInvoiceFile ||
        hasMissingInvoiceNumber ||
        hasInvalidInvoiceNumber ||
        hasMissingDate ||
        hasInvalidDate
      ) {
        return;
      }
    }

    if (data.taxDocumentType === 'invoiceNotIssued') {
      if (!fileAmount || fileAmount <= 0) {
        toast(
          <Toast
            title="Atenção!"
            content="Informe a quantidade de documentos que serão entregues ao motorista no momento da coleta no campo “quantidade de documentos”."
            type="error"
          />,
          options
        );

        return;
      }
    }

    if (otherFiles?.length > 0) {
      let hasMissingDocumentType = false;
      const newOtherFiles = [...otherFiles];

      otherFiles?.forEach((file, index) => {
        if (file.documentType === '') {
          newOtherFiles[index].hasDocumentTypeError = true;
          hasMissingDocumentType = true;
        }
      });

      if (hasMissingDocumentType) {
        setOtherFiles(newOtherFiles);

        toast(
          <Toast
            title="Atenção!"
            type="error"
            content="Campo “Tipo de Documento” não preenchido."
          />,
          options
        );

        return;
      }
    }

    setIsSubmitting(true);
  };

  const showToastDeclaration = () => {
    toast(
      <Toast
        title="Atenção!"
        content="Informe a quantidade de declarações que serão preenchidas no campo “quantidade de documentos”."
        type="error"
      />,
      options
    );
  };

  useEffect(() => {
    if (!hasAcceptResponsibilityTerms && documentType === 'declaration') {
      setValue('taxDocumentType', '');
      setDocumentType('');
    }
  }, [hasAcceptResponsibilityTerms]);

  useEffect(() => {
    setValue('taxDocumentType', documentType);

    documentType === 'invoiceNotIssued' &&
      setValue('acceptedInvoiceNotIssued', acceptedInvoiceNotIssued);

    if (!enableDeclaration && documentType === 'declaration') {
      setDocumentType('');
      setDeclarationFiles([]);
    }
  }, []);

  return (
    <form onSubmit={handleSubmit(onNextStep)}>
      <Paper
        elevation={2}
        style={{
          position: 'relative',
          marginTop: 16,
          padding: '20px 16px 16px'
        }}
      >
        <Stack spacing={3} style={{ width: '100%', marginBottom: 16 }}>
          <Title>Informações Fiscais:</Title>

          <TooltipWrapper>
            <TextField
              label="Quantidade de documentos *"
              value={fileAmount}
              onChange={(e) => {
                const regex = /^[0-9]+$/;
                if (!e.target.value.match(regex) && e.target.value !== '') {
                  return;
                }
                handleSetFileAmount(e.target.value);
              }}
              style={{ width: maxSM ? '100%' : 240 }}
              inputProps={{ min: 0, maxLength: 5 }}
            />
            <TooltipIcon
              title="Informar a quantidade de documentos que serão anexados nesta solicitação!"
              placement="top"
              className="tooltip"
            >
              <i className="far fa-question-circle" />
            </TooltipIcon>
          </TooltipWrapper>
        </Stack>

        <Stack spacing={2} style={{ width: '100%', alignItems: 'flex-start' }}>
          <Stack
            direction={maxMD ? 'column' : 'row'}
            spacing={maxMD ? 0 : 2}
            alignItems="flex-start"
          >
            <Stack>
              <TooltipWrapper>
                <p style={{ marginTop: 11 }}>Tipo de documento fiscal *:</p>
                <TooltipIcon
                  title="Deve selecionar o tipo de documento fiscal que será enviado junto com a mercadoria!"
                  placement="top"
                  className="tooltip"
                >
                  <i className="far fa-question-circle" />
                </TooltipIcon>
              </TooltipWrapper>
              <FormHelperText
                error={!!errors.taxDocumentType}
                style={{ marginTop: 8 }}
              >
                {errors.taxDocumentType?.message}
              </FormHelperText>
            </Stack>
            <Controller
              control={control}
              defaultValue=""
              name="taxDocumentType"
              render={({ field }) => {
                return (
                  <RadioGroup
                    {...field}
                    style={{
                      flexDirection: maxSM ? 'column' : 'row',
                      gap: maxSM ? 0 : 16
                    }}
                  >
                    <FormControlLabel
                      value="declaration"
                      control={
                        // eslint-disable-next-line react/jsx-wrap-multilines
                        <Radio
                          checked={documentType === 'declaration'}
                          disabled={
                            !acceptDeclaration ||
                            !enableDeclaration ||
                            hasCreatePickupWithInvoice
                          }
                          onClick={() => {
                            if (declarationFiles.length < 1) {
                              fileAmount < 1
                                ? showToastDeclaration()
                                : openAcceptResponsibilityTermsModal();
                            } else {
                              handleChangeRadio('declaration');
                            }
                          }}
                        />
                      }
                      label="Declaração"
                    />
                    <FormControlLabel
                      value="invoiceIssued"
                      control={
                        // eslint-disable-next-line react/jsx-wrap-multilines
                        <Radio
                          checked={documentType === 'invoiceIssued'}
                          onChange={(e) => handleChangeRadio(e.target.value)}
                        />
                      }
                      label="Nota fiscal emitida"
                    />
                    <FormControlLabel
                      value="invoiceNotIssued"
                      control={
                        // eslint-disable-next-line react/jsx-wrap-multilines
                        <Radio
                          checked={documentType === 'invoiceNotIssued'}
                          disabled={hasCreatePickupWithInvoice}
                          onClick={() => {
                            if (documentType !== 'invoiceNotIssued') {
                              openAcceptInvoiceNotIssuedModal();
                            }
                          }}
                          onChange={(e) => handleChangeRadio(e.target.value)}
                        />
                      }
                      label="Nota fiscal a emitir"
                    />
                  </RadioGroup>
                );
              }}
            />
          </Stack>

          {documentType === 'declaration' && (
            <Stack
              direction="column"
              spacing={2}
              style={{ width: '100%' }}
              divider={<Divider orientation="horizontal" flexItem />}
            >
              <Declaration />
              <OtherFiles />
            </Stack>
          )}

          {documentType === 'invoiceIssued' && (
            <Stack
              direction="column"
              spacing={2}
              style={{ width: '100%' }}
              divider={<Divider orientation="horizontal" flexItem />}
            >
              <Stack
                gap={2}
                style={{ width: '100%' }}
                divider={
                  invoiceFiles?.filter(({ file }) => !!file)?.length > 0 && (
                    <Divider orientation="horizontal" flexItem />
                  )
                }
              >
                <BoxButton style={{ display: 'flex' }}>
                  <LabelFileInput
                    htmlFor="invoice-files"
                    style={{ minWidth: 256 }}
                    className={
                      fileAmount === '' ? 'btnDanfeDisabled' : 'btnDanfe'
                    }
                  >
                    ANEXAR DANFE (PDF ou XML)
                  </LabelFileInput>
                  <FileInput
                    ref={inputFileRef}
                    id="invoice-files"
                    type="file"
                    accept=".pdf, .xml"
                    multiple
                    onChange={handleAddInvoiceFiles}
                    disabled={fileAmount === ''}
                  />
                </BoxButton>

                {invoiceFiles?.filter(({ file }) => !!file)?.length > 0 && (
                  <FileList />
                )}
              </Stack>
              <OtherFiles />
            </Stack>
          )}

          {documentType === 'invoiceNotIssued' && (
            <Stack direction="column" spacing={2} style={{ width: '100%' }}>
              <Box style={{ width: '100%' }}>
                <Controller
                  name="acceptedInvoiceNotIssued"
                  control={control}
                  defaultValue={acceptedInvoiceNotIssued}
                  render={({ field }) => (
                    <Stack>
                      <FormControlLabel
                        label="Ciente que a Nota Fiscal deve ser entregue ao motorista no ato da coleta!"
                        style={{ color: '#004e9a' }}
                        control={
                          // eslint-disable-next-line react/jsx-wrap-multilines
                          <Checkbox
                            {...field}
                            checked={acceptedInvoiceNotIssued}
                            onClick={() => {}}
                          />
                        }
                      />
                      <FormHelperText
                        error={!!errors.acceptedInvoiceNotIssued}
                        style={{ marginLeft: 32 }}
                      >
                        {errors.acceptedInvoiceNotIssued?.message}
                      </FormHelperText>
                    </Stack>
                  )}
                />
              </Box>
              <OtherFiles />
            </Stack>
          )}
        </Stack>
      </Paper>

      <StepButtons nextFunction={handleSubmit(onNextStep)} />
    </form>
  );
};
